<template lang="pug">
#room-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 部屋情報修正

    .box-body.register-body
      FormInput(
        v-model="name"
        key-name="部屋名" name="room-name" type="text" placeholder="部屋名"
        :validation-rules="{ required: true, max: 32 }" :maxlength="32"
        :styles="{ width: '340px' }" required)
      FormInputRadio(
        v-model="useType"
        keyName="部屋用途" name="use-type" :options="useTypeOptions"
        :validationRules="{ required: true }" required)
      FormInputRadio(
        v-model="roomType"
        keyName="部屋種別" name="room-type" :options="roomTypeOptions"
        :validationRules="{ required: true }" required)
      FormInput(
        v-if="roomType == 3 || roomType == 4"
        v-model="capacity"
        key-name="部屋定員" name="capacity" type="text" placeholder="定員数を半角数字で入力してください"
        :validation-rules="{ required: true, numeric: true }" :maxlength="3"
        :styles="{ width: '340px' }" required)
      FormInputSelect(
        key-name="利用者グループ"
        name="group-id"
        v-model="residentGroupId"
        :options="residentGroupOptions"
        :styles="{ 'max-width': '540px' }"
        :validationRules="{ required: true }" required)
        template(#optionText)
          span ※設定すると、一覧表示のフィルタリングで使用できます

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.register-btn(@click="showConfirmModal()" type="button") 更新
        button.btn.cancel-btn(@click="gotoRoomList()" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="部屋情報を更新しますか？" @ok="updateRoom")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoRoomList")
  
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { SendWaiterMessagesState } from "@/store/NeedRefreshes";

import CustomValidate from "@/commons/utils/CustomValidate";
import { PcUrl } from "@/commons/constants/PcUrl";
import RoomsService, { RoomGetModel, RoomPatchModel, RoomType, UseType } from "@/commons/services/Rooms";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormInputRadio from "@/pc/components/forms/FormInputRadio.vue";
import SystemError from "@/commons/models/SystemError";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";

/**
 * ### 部屋更新画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    ValidationAlert,
    FormInput,
    FormInputRadio,
    FormInputSelect
  },
  $_veeValidate: { validator: "new" }
})
export default class RoomEdit extends Vue {
  /** 部屋サービス */
  @Inject("RoomsService") roomsService!: RoomsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 部屋名 */
  name: string = "";
  /** 用途 */
  useType: UseType | null = null;
  /** 用途選択項目 */
  useTypeOptions: Array<{ value: number | string; label: string }> = [];
  /** 部屋種別 */
  roomType: RoomType | null = null;
  /** 部屋種別選択項目 */
  roomTypeOptions: Array<{ value: number | string; label: string }> = [];
  /** 人数 */
  capacity: number | null = null;
  /** 利用者グループID */
  residentGroupId: number | null = null;
  /** グループセレクトボックス用項目 */
  residentGroupOptions: Array<{ value: number | string; label: string }> = [];
  /** 更新日時 */
  updated: number | null = null;

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle created
   */
  public created(): void {
    CustomValidate.addCustomValidate(this.$validator);

    return;
  }

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const useTypeOptions: Array<{ value: number | string; label: string }> = this.createUseTypeOptions();
    this.useTypeOptions = useTypeOptions;

    const roomTypeOptions: Array<{ value: number | string; label: string }> = this.createRoomTypeOptions();
    this.roomTypeOptions = roomTypeOptions;

    const residentGroups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const residentGroupOptions: Array<{ value: number; label: string }> = this.createResidentGroupOptions(residentGroups);
    this.residentGroupOptions = residentGroupOptions;

    const roomId: number = Number(this.$route.params.id);
    const room: RoomGetModel = await this.getRoom(roomId);

    this.name = room.name;
    this.useType = room.useType;
    this.roomType = room.roomType;
    this.capacity = room.capacity;
    this.residentGroupId = room.residentGroupId;
    this.updated = room.updated;

    this.isBusy = false;

    return;
  }

  /**
   * ### 用途セレクトボックス用項目を作成する
   */
  public createUseTypeOptions(): Array<{ value: number; label: string }> {
    let useTypeOptions: Array<{ value: number; label: string }> = [];

    useTypeOptions.push({ value: UseType.UNMANAGED, label: "空床管理対象外" });
    useTypeOptions.push({ value: UseType.NORMAL_STAY, label: "入居用部屋" });
    useTypeOptions.push({ value: UseType.SHORT_STAY, label: "ショートステイ" });

    return useTypeOptions;
  }

  /**
   * ### 部屋種別セレクトボックス用項目を作成する
   */
  public createRoomTypeOptions(): Array<{ value: number; label: string }> {
    let roomTypeOptions: Array<{ value: number; label: string }> = [];

    roomTypeOptions.push({ value: RoomType.UNMANAGED, label: "空床管理対象外" });
    roomTypeOptions.push({ value: RoomType.SINGLE, label: "個室" });
    roomTypeOptions.push({ value: RoomType.DOUBLE, label: "２人部屋（夫婦部屋）" });
    roomTypeOptions.push({ value: RoomType.MULTI_MEN, label: "多床室（男性）" });
    roomTypeOptions.push({ value: RoomType.MULTI_WOMEN, label: "多床室（女性）" });

    return roomTypeOptions;
  }

  /**
   * ### 利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### グループセレクトボックス用項目を作成する
   *
   * - アカウントの権限に応じて選択肢をマスキングする
   */
  public createResidentGroupOptions(residentGroups: Array<ResidentGroupsGetModel>): Array<{ value: number; label: string }> {
    let residentGroupOptions: Array<{ value: number; label: string }> = [];

    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (allowedGroupsMap.has(group.id)) {
        residentGroupOptions.push({
          value: group.id,
          label: group.name
        });
      }
    }

    return residentGroupOptions;
  }

  /**
   * ### 部屋を取得する
   */
  public async getRoom(roomId: number): Promise<RoomGetModel> {
    const response: RoomGetModel = await this.roomsService.get(userStore, roomId);
    return response;
  }

  /**
   * ### 更新確認モーダルを表示する
   *
   * - 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   */
  public async showConfirmModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();

    if (validationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### 部屋更新
   */
  public async updateRoom(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const roomId: number = Number(this.$route.params.id);
      const requestBody: RoomPatchModel = this.createRequestBody();
      await this.roomsService.patch(userStore, roomId, requestBody);

      // メッセージ送信(待機者)のテンプレートが変更になるので再読み込みさせるようstoreを更新
      let sendWaiterMessageState: SendWaiterMessagesState = needRefreshesStore.sendWaiterMessage;
      sendWaiterMessageState.sendMessages = true;
      needRefreshesStore.setSendWaiterMessage(sendWaiterMessageState);

      this.showUpdateSuccessModal = true;
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409 && 0 < systemError.originalResponse.indexOf("same room name is already registered.")) {
        await this.createView();
        this.errorMessage = "同一の部屋名が既に登録されているため、更新できません";
      } else if (systemError.statusCode == 400 && 0 < systemError.originalResponse.indexOf("residentGroupId is not exist.")) {
        await this.createView();
        this.errorMessage = "利用者グループが削除されているため、登録できませんでした";
      } else if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 部屋更新用のリクエストボディを作成する
   */
  public createRequestBody(): RoomPatchModel {
    const requestBody: RoomPatchModel = {
      name: this.name,
      useType: this.useType as UseType,
      roomType: this.roomType as RoomType,
      capacity: this.calculateCapacity(),
      residentGroupId: this.residentGroupId as number,
      updated: this.updated as number
    };

    return requestBody;
  }

  /**
   * ### 人数を計算する
   *
   * - 部屋種別に応じて返却する人数を計算する
   *   - 管理対象外：0
   *   - 個室：1
   *   - 二人部屋：2
   *   - 多床室（男性）、多床室（女性）：画面入力された人数
   */
  public calculateCapacity(): number {
    switch (this.roomType) {
      case RoomType.UNMANAGED: {
        return 0;
      }
      case RoomType.SINGLE: {
        return 1;
      }
      case RoomType.DOUBLE: {
        return 2;
      }
      case RoomType.MULTI_MEN:
      case RoomType.MULTI_WOMEN: {
        return Number(this.capacity);
      }
      default: {
        return 0;
      }
    }
  }

  /**
   * ### 部屋一覧に遷移する
   */
  public gotoRoomList(): void {
    this.$router.push(PcUrl.RoomList);

    return;
  }
}
</script>

<style lang="scss" scoped>
#room-edit {
  max-width: 880px;
  margin-left: auto;
  margin-right: auto;

  .register-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
